import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBuildingRoster } from '../../../../api/hierarchy-client';
import {
  BuildingRoster,
  VisitorInside,
} from '../../../../interfaces/hierarchy-interfaces';
import { ListSkeleton } from '../dashboard-shared-components/load-state';
import { Staleable, StateSwitch, listItemMotion } from '../../../../motion';
import {
  Banner,
  Empty,
  EmptyText,
  EmptyTitle,
  GhostButton,
  Header,
  HeaderActions,
  HeaderText,
  InlineRefreshButton,
  Input,
  Label,
  PrimaryButton,
  ResultCount,
  RosterBody,
  RosterCard,
  RosterGrid,
  RosterMeta,
  RosterName,
  RosterSection,
  RosterUnit,
  SectionCount,
  SectionHead,
  SectionNote,
  SectionTitle,
  Select,
  Sub,
  Tag,
  Title,
  Toolbar,
  ToolbarField,
  ToolbarSpacer,
  VisitorCard,
  VisitorWhen,
  VisitorWhenDay,
  Wrap,
} from './dashboard-units.styled';
import {
  formatDate,
  groupByPortfolio,
  occupantFullName,
  readError,
  relationshipLabel,
  useBuildings,
  useRememberedBuilding,
} from './units-shared';

/**
 * WHO'S HOME — the front desk's "who is in this building right now" board.
 *
 * ==========================================================================
 * TWO SECTIONS. NEVER ONE LIST, NEVER ONE NUMBER. (WP-32, owner decision)
 * ==========================================================================
 * RESIDENTS are open occupancies: people who LIVE here. VISITORS INSIDE are
 * open arrivals: people who WALKED IN and have no departure recorded. "Lives
 * here" and "walked in at 14:02" are different kinds of fact, they are wrong in
 * different ways, and a total of the two answers no question anybody asked — so
 * they get separate headings, separate counts and separate empty states, and
 * there is no combined figure anywhere on this screen.
 *
 * Both halves come from ONE request so they describe ONE instant (`as_of`).
 * Two requests would let the screen show a visitor who was signed out between
 * them, which is the exact failure this board exists to prevent.
 *
 * HONESTY ABOUT STALE DATA. `departed_at IS NULL` means only that nobody ever
 * recorded a departure. An arrival still open from a PREVIOUS local day was
 * never observed leaving and has not been confirmed present either — it is a
 * desk-process gap, not a person in the lobby. Those rows are marked, counted
 * separately and called out above the list. They are never hidden: clearing
 * them is the work this screen is asking for.
 *
 * ONE DESTINATION. This is the only Who's Home surface in the product. It
 * renders inside /dashboard (owner / property manager) and as the body of the
 * standalone /roster page a concierge reaches from the main menu, so the two
 * things the host may want to own are props. Anything in the menu that wants to
 * show who is inside links to /roster; it does not grow a second screen.
 */

export interface DashboardBuildingRosterProps {
  // Controlled building selection. Passing it (even as null) hands the choice to
  // the host, which is then also responsible for rendering a picker — the
  // roster hides its own so the screen never shows two.
  buildingId?: number | null;
  // "Manage units" jumps to /dashboard/units, a route only owners and property
  // managers may open. Offering it to a concierge is a guaranteed bounce back
  // out, so a host that serves other roles turns it off.
  showManageUnits?: boolean;
  // Only meaningful alongside a controlled buildingId: it tells the empty state
  // whether the host has a building picker ON SCREEN. An empty roster may only
  // suggest "switch it above" when there is actually something above to switch,
  // and a controlled host may hide its picker (roster-page.tsx does, with one
  // building). Uncontrolled hosts are answered from `buildings` directly.
  hostShowsBuildingPicker?: boolean;
}

// `arrived_local` is 'YYYY-MM-DD HH:mm' ALREADY CONVERTED by the server, on the
// clock its answer names (`tz`). It is split, never re-parsed into a Date:
// `new Date()` on it would reinterpret the string in the BROWSER's zone, which
// is how a desk in another country ends up reading 02:10 for a 20:10 arrival.
//
// The board itself now formats from the INSTANT (`arrived_at`) in the clock of
// the surface it is drawn on — see arrivalClock — because the desk shows the
// building's clock to everybody, an owner included, and the server's text for
// an owner is on the owner's. This stays as the fallback for a row with no
// instant.
export const splitArrivalLocal = (
  value: string | undefined
): { day: string; time: string } => {
  const [day = '', time = ''] = (value ?? '').split(' ');

  return { day, time };
};

// The day label on a visitor card. Somebody who came in today is placed by the
// CLOCK; somebody still open from an earlier day is placed by the CALENDAR,
// because the time of day stopped being the useful fact about them.
export const arrivalDayLabel = (visitor: VisitorInside, zone?: string): string => {
  if (!visitor.stale) {
    return 'Today';
  }

  const { day } = arrivalClock(visitor, zone);
  if (!isDayKey(day)) return day;
  // The year is noise on a card this small unless the row really is that old,
  // and if it is, it should be loud.
  const thisYear = dayKeyIn(new Date(), zone).slice(0, 4);

  return day.startsWith(thisYear)
    ? formatDayKey(day, { day: '2-digit', month: 'short' })
    : formatDayKey(day, { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * WHEN A VISITOR CAME IN, on the clock the board is drawn on: the day key and
 * the HH:mm, from the instant. Falls back to the server's own text for a row
 * that carries no instant.
 */
export const arrivalClock = (
  visitor: Pick<VisitorInside, 'arrived_at' | 'arrived_local'>,
  zone?: string
): { day: string; time: string } => {
  const day = visitor.arrived_at ? dayKeyIn(visitor.arrived_at, zone) : '';
  if (!day) return splitArrivalLocal(visitor.arrived_local);

  return { day, time: clockTime(visitor.arrived_at, zone) };
};

// How long a ghost has been open, in the words a desk would use.
/* IMPORTED and re-exported, not redefined. The wording lives in
 * src/components/presence/presence-format.ts so this board and the sign-out
 * screen cannot drift apart — they already had, in both wording and the
 * days_open boundary.
 *
 * Note the import: a bare `export { x } from '...'` re-exports the name for
 * OTHER modules but never binds it in this one, and this file calls
 * staleAgeLabel itself further down. */
import { staleAgeLabel } from '../../../presence/presence-format';
import {
  clockTime,
  dayKeyIn,
  formatDayKey,
  isDayKey,
} from '../../../../function-utils/building-time';
import { useClock } from '../../../../context/clock-context';
import { ClockNote } from '../../../clock-note/clock-note';

export { staleAgeLabel };

// The instant BOTH sections describe, on the clock of the surface the board is
// drawn on (useClock): the building's at the desk, the reader's own on an
// owner's or a manager's dashboard. One zone for the header and the cards,
// never two — and the clock note beside it says whose it is.
//
// 24-hour, deliberately: a header reading "02:02 PM" beside cards reading
// "14:02" makes a reader stop and work out whether they are looking at the same
// clock. An unusable zone does not blank the clock on a live board — the one
// time module falls back to the device's.
export const formatAsOf = (iso?: string, timezone?: string): string =>
  iso ? clockTime(iso, timezone) : '';


// Match on name OR unit: at a front desk the person asking knows one or the
// other, never reliably both. One search box drives BOTH sections, because
// "is Maria here?" must not need the asker to know whether Maria lives here.
const visitorMatches = (visitor: VisitorInside, term: string): boolean => {
  if (visitor.people.some(name => name.toLowerCase().includes(term))) {
    return true;
  }

  return (visitor.unit_label ?? '').toLowerCase().includes(term);
};



export const DashboardBuildingRoster: React.FC<
  DashboardBuildingRosterProps
> = ({
  buildingId: controlledBuildingId,
  showManageUnits = true,
  hostShowsBuildingPicker = false,
}) => {
  const navigate = useNavigate();
  // `undefined` means "nobody told us", i.e. uncontrolled. `null` is a real
  // controlled value meaning "the host has no building selected yet".
  const isControlled = controlledBuildingId !== undefined;
  const {
    buildings,
    loading: buildingsLoading,
    error: buildingsError,
  } = useBuildings(!isControlled);
  // null when grouping would add nothing (one portfolio, or no portfolio:read).
  const buildingGroups = useMemo(
    () => groupByPortfolio(buildings),
    [buildings]
  );
  // Shared with the Units screen: staff working one building all day should
  // never have to re-pick it when moving between the two views.
  const [ownBuildingId, setOwnBuildingId] = useRememberedBuilding();
  const buildingId = isControlled
    ? controlledBuildingId ?? null
    : ownBuildingId;

  const [roster, setRoster] = useState<BuildingRoster | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (isControlled || buildingsLoading || buildings.length === 0) {
      return;
    }

    const stillVisible = buildings.some(b => b.id === ownBuildingId);

    if (!stillVisible) {
      setOwnBuildingId(buildings[0].id);
    }
  }, [
    isControlled,
    buildings,
    buildingsLoading,
    ownBuildingId,
    setOwnBuildingId,
  ]);

  /**
   * A BOARD THAT STATES ITS READ TIME MUST BE ABLE TO MAKE IT CURRENT.
   *
   * This fetched once per buildingId and never again — no interval, no focus
   * listener, no control — while printing "Read at 14:02" as its own honesty
   * mechanism. Saying when you last looked, with no way to look again, tells the
   * desk their information is stale and then strands them with it. `reloadTick`
   * is what the Refresh button below turns.
   */
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!buildingId) {
      setRoster(null);

      return;
    }

    let cancelled = false;

    setLoading(true);
    setError('');
    getBuildingRoster(buildingId)
      .then(data => {
        if (!cancelled) {
          setRoster(data);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setRoster(null);
          setError(readError(err, 'Could not load the roster.'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [buildingId, reloadTick]);

  const term = query.trim().toLowerCase();

  const residents = useMemo(() => {
    const rows = roster?.residents ?? [];

    if (!term) {
      return rows;
    }

    return rows.filter(resident => {
      const name = occupantFullName(resident).toLowerCase();
      const unit = (resident.unit_number ?? '').toLowerCase();

      return name.includes(term) || unit.includes(term);
    });
  }, [roster, term]);

  // `null` and `[]` are DIFFERENT ANSWERS and the difference is load-bearing:
  // null (the key absent) means the caller may not read visitor entries at all,
  // [] means they may and nobody is inside. Collapsing them would turn "you
  // can't see this" into the reassuring lie "nobody is here".
  const allVisitors = roster?.visitors_inside ?? null;
  const maySeeVisitors = allVisitors !== null;

  const visitors = useMemo(() => {
    if (!allVisitors) {
      return [];
    }

    if (!term) {
      return allVisitors;
    }

    return allVisitors.filter(visitor => visitorMatches(visitor, term));
  }, [allVisitors, term]);

  // The building's own name, taken from the RESPONSE and never from
  // `buildings`: on /roster this component runs controlled, so useBuildings is
  // disabled and `buildings` is empty, whereas building_name comes back on
  // every successful call including one that found nobody. Naming the building
  // is the whole point — it is what makes "this building is empty" read
  // differently from "you are looking at the wrong building".
  const buildingName = roster?.building_name || 'this building';
  // Non-archived units in the building, placeholders included. Defaulted so the
  // branch below is total even if the response predates unit_count.
  const unitCount = roster?.unit_count ?? 0;
  const residentTotal = roster?.count ?? 0;
  const visitorTotal = allVisitors?.length ?? 0;
  // Counted over EVERY open arrival, not over the filtered list: how many
  // people this building has lost track of is a fact about the building, and it
  // must not change because somebody typed a name into a search box.
  const staleTotal = roster?.visitors_inside_stale_count ?? 0;
  const arrivedTodayTotal = visitorTotal - staleTotal;
  // A search miss only exists when there was something to miss. With a query
  // typed against a section that holds nobody at all, the honest answer is that
  // section's own empty state, not "no one matches that".
  const residentSearchMiss = Boolean(term) && residentTotal > 0;
  const visitorSearchMiss = Boolean(term) && visitorTotal > 0;
  // Say "switch it above" only when a picker is genuinely on screen with more
  // than one option: uncontrolled we render our own (and one building is not a
  // choice), controlled the host tells us.
  const canSwitchBuilding = isControlled
    ? hostShowsBuildingPicker
    : buildings.length > 1;
  /* WHOSE CLOCK THE BOARD IS ON (R-T12). At the desk: the building's, for
     whoever is standing there — `day_tz` is the building's own zone whatever
     clock the server answered an owner on. On a dashboard: the clock the
     response carries. `viewer` is no longer "this building never had a zone";
     it is what every owner and manager is answered. */
  const clock = useClock({
    buildingId,
    buildingZone: roster?.day_tz,
    response: roster
      ? {
          tz: roster.tz || roster.timezone,
          tz_source: roster.tz_source || roster.timezone_source,
        }
      : null,
  });
  const asOf = formatAsOf(roster?.as_of, clock.zone);

  const renderResidents = () => {
    if (residents.length > 0) {
      return (
        <RosterGrid>
          {residents.map((resident, index) => (
            <RosterCard
              key={resident.id}
              {...listItemMotion(index)}
              layout={false}
            >
              <RosterUnit>{resident.unit_number ?? '—'}</RosterUnit>
              <RosterBody>
                <RosterName>{occupantFullName(resident)}</RosterName>
                <RosterMeta>
                  {relationshipLabel(resident.relationship)}
                  {resident.is_primary && <Tag tone="brand">Primary</Tag>}
                  {/* An occupant with no account can never be reached through
                      the portal — the desk needs to know that up front. */}
                  {resident.user_id === null && (
                    <Tag tone="neutral">No login</Tag>
                  )}
                </RosterMeta>
                <RosterMeta>Since {formatDate(resident.moved_in)}</RosterMeta>
              </RosterBody>
            </RosterCard>
          ))}
        </RosterGrid>
      );
    }

    /* Three different kinds of nothing, and they need three different answers.
       Telling them apart needs unit_count, which is why the route returns it:
       an empty section in a building with no units is a setup step, an empty
       section in a building full of units is a fact about today, and an empty
       list under a search term is neither. */
    return (
      <Empty>
        {residentSearchMiss ? (
          /* 1. The search is hiding everyone. Copy kept verbatim — it was
                already right, and the Clear button is the offer. */
          <>
            <EmptyTitle>No one matches that</EmptyTitle>
            <EmptyText>
              Try part of a surname, or just the unit number.
            </EmptyText>
            <GhostButton type="button" onClick={() => setQuery('')}>
              Clear search
            </GhostButton>
          </>
        ) : unitCount === 0 ? (
          /* 2. No units at all. Nobody can be checked in to a building with no
                doors, so the answer is a setup step, not an explanation about
                occupancies. */
          <>
            <EmptyTitle>No units in {buildingName} yet</EmptyTitle>
            <EmptyText>
              This section is the front desk&apos;s live list of who is behind
              each door right now — open occupancies only, never past residents.
            </EmptyText>
            <EmptyText>
              {buildingName} has no units on it yet, so there is nowhere for
              anyone to live. Add the units first and residents can be moved in
              to them.
            </EmptyText>
            {showManageUnits ? (
              /* Only ever inside showManageUnits: /dashboard/units is closed to
                 a concierge and the dashboard guard bounces them. */
              <PrimaryButton
                type="button"
                onClick={() => navigate('/dashboard/units')}
              >
                Add units
              </PrimaryButton>
            ) : (
              <EmptyText>
                An owner or property manager sets the units up for this building.
              </EmptyText>
            )}
          </>
        ) : (
          /* 3. Doors, but nobody behind them. The count is the reassurance that
                the building itself is set up correctly. */
          <>
            <EmptyTitle>Nobody is checked in at {buildingName}</EmptyTitle>
            <EmptyText>
              {unitCount} {unitCount === 1 ? 'unit' : 'units'} here, none with a
              resident right now.
            </EmptyText>
            <EmptyText>
              This section only ever shows open occupancies. A name appears the
              moment someone is moved in to a unit and drops off the moment they
              are moved out — past residents stay on each unit&apos;s history.
            </EmptyText>
            {canSwitchBuilding && (
              <EmptyText>
                Expecting somebody? Check you are on the right building — you can
                switch it above.
              </EmptyText>
            )}
            {showManageUnits && (
              <GhostButton
                type="button"
                onClick={() => navigate('/dashboard/units')}
              >
                Open Units
              </GhostButton>
            )}
          </>
        )}
      </Empty>
    );
  };

  const renderVisitors = () => {
    // Not an empty list — an unreadable one. Said out loud so nobody reads a
    // permission boundary as "the lobby is clear".
    if (!maySeeVisitors) {
      return (
        <Empty>
          <EmptyTitle>Visitor entries are not part of your access</EmptyTitle>
          <EmptyText>
            You can see who lives here, but not who has signed in. Ask an owner
            or property manager for the visitor-log permission if you need both
            halves of this screen.
          </EmptyText>
        </Empty>
      );
    }

    if (visitors.length > 0) {
      return (
        <RosterGrid>
          {visitors.map((visitor, index) => {
            const { time } = arrivalClock(visitor, clock.zone);
            const who = visitor.people.length
              ? visitor.people.join(', ')
              : 'Name not recorded';

            return (
              <VisitorCard
                key={visitor.id}
                {...listItemMotion(index)}
                layout={false}
                stale={visitor.stale}
              >
                <VisitorWhen stale={visitor.stale}>
                  {time || '—'}
                  <VisitorWhenDay>{arrivalDayLabel(visitor, clock.zone)}</VisitorWhenDay>
                </VisitorWhen>
                <RosterBody>
                  <RosterName>{who}</RosterName>
                  <RosterMeta>
                    {visitor.unit_label ? (
                      <span>Visiting {visitor.unit_label}</span>
                    ) : (
                      <span>No unit recorded</span>
                    )}
                    {visitor.visitor_type && (
                      <Tag tone="neutral">{visitor.visitor_type}</Tag>
                    )}
                    {/* One entry can be a party of six. The row count and the
                        headcount are both true and answer different questions,
                        so the headcount is shown rather than substituted. */}
                    {typeof visitor.headcount === 'number' &&
                      visitor.headcount > 1 && (
                        <Tag tone="neutral">{visitor.headcount} people</Tag>
                      )}
                  </RosterMeta>
                  {visitor.stale && (
                    <RosterMeta>
                      {/* On the ROW, not only in the note above the list: a
                          reader scanning cards must not be able to mistake a
                          ghost for somebody on this shift. */}
                      {/* $wrap: this pill is a SENTENCE, not a label, and at
                          320px its 242px of nowrap text pushed the whole page
                          55px sideways. See Tag in dashboard-units.styled.tsx. */}
                      <Tag tone="warning" $wrap>
                        Never signed out · {staleAgeLabel(visitor)}
                      </Tag>
                    </RosterMeta>
                  )}
                </RosterBody>
              </VisitorCard>
            );
          })}
        </RosterGrid>
      );
    }

    return (
      <Empty>
        {visitorSearchMiss ? (
          <>
            <EmptyTitle>No visitor matches that</EmptyTitle>
            <EmptyText>
              Try part of a name, or the unit they came to see.
            </EmptyText>
            <GhostButton type="button" onClick={() => setQuery('')}>
              Clear search
            </GhostButton>
          </>
        ) : (
          <>
            <EmptyTitle>Nobody has signed in without signing out</EmptyTitle>
            <EmptyText>
              Every visitor logged at {buildingName} has a departure recorded, so
              there is nobody outstanding.
            </EmptyText>
            <EmptyText>
              A name appears here the moment a visitor is logged in and drops off
              the moment the desk signs them out.
            </EmptyText>
          </>
        )}
      </Empty>
    );
  };

  return (
    <Wrap>
      <Header>
        <HeaderText>
          {/* The page and the menu tile that opens it say the SAME words. Two
              names for one screen is how a product ends up with two of it. */}
          <Title>Who&apos;s Home</Title>
          <Sub>
            Two separate facts, kept apart on purpose: the people who live here,
            and the visitors who signed in and were never signed out. Do not add
            them together.
          </Sub>
        </HeaderText>
        {showManageUnits && (
          <HeaderActions>
            <GhostButton
              type="button"
              onClick={() => navigate('/dashboard/units')}
            >
              Manage units
            </GhostButton>
          </HeaderActions>
        )}
      </Header>

      <Toolbar>
        {!isControlled && (
          <ToolbarField>
            <Label htmlFor="roster-building">Building</Label>
            <Select
              id="roster-building"
              value={ownBuildingId ?? ''}
              disabled={buildingsLoading || buildings.length === 0}
              onChange={event =>
                setOwnBuildingId(
                  event.target.value ? Number(event.target.value) : null
                )
              }
            >
              {buildingsLoading && <option value="">Loading buildings…</option>}
              {!buildingsLoading && buildings.length === 0 && (
                <option value="">No buildings available</option>
              )}
              {/* Grouped by portfolio once there is more than one. */}
              {buildingGroups
                ? buildingGroups.map(group => (
                    <optgroup key={group.portfolio} label={group.portfolio}>
                      {group.buildings.map(building => (
                        <option key={building.id} value={building.id}>
                          {building.name}
                        </option>
                      ))}
                    </optgroup>
                  ))
                : buildings.map(building => (
                    <option key={building.id} value={building.id}>
                      {building.name}
                    </option>
                  ))}
            </Select>
          </ToolbarField>
        )}

        <ToolbarField>
          <Label htmlFor="roster-search">Find a person</Label>
          <Input
            id="roster-search"
            type="search"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Name or unit number"
            autoComplete="off"
          />
        </ToolbarField>

        <ToolbarSpacer />

        {/* One timestamp for the whole screen, because both sections were read
            at that one instant. A "who is inside" board with no clock on it
            invites a page loaded before lunch being read as the truth now. */}
        {!loading && roster && asOf && (
          <ResultCount>
            Read at {asOf} · <ClockNote clock={clock} inline />{' '}
            <InlineRefreshButton
              type="button"
              onClick={() => setReloadTick(tick => tick + 1)}
            >
              Refresh
            </InlineRefreshButton>
          </ResultCount>
        )}
      </Toolbar>

      {buildingsError && <Banner tone="alert">{buildingsError}</Banner>}
      {error && <Banner tone="alert">{error}</Banner>}

      {/* First read: a skeleton in the box the cards will fill. Refresh: the
          cards stay and dim until the new read lands. */}
      <StateSwitch
        state={
          loading && !roster ? 'loading' : !buildingId ? 'pick' : 'ready'
        }
        minHeight={240}
      >
      {loading && !roster ? (
        <ListSkeleton rows={4} label="roster" />
      ) : !buildingId ? (
        <Empty>
          <EmptyTitle>Pick a building</EmptyTitle>
          <EmptyText>Choose a building above to see who is there.</EmptyText>
        </Empty>
      ) : (
        <Staleable stale={loading}>
          <RosterSection aria-label="Residents">
            <SectionHead>
              <SectionTitle>Residents</SectionTitle>
              <SectionCount>
                {term
                  ? `${residents.length} of ${residentTotal} shown`
                  : `${residentTotal} ${
                      residentTotal === 1 ? 'resident' : 'residents'
                    }`}
              </SectionCount>
            </SectionHead>
            <SectionNote>
              People who live here — open occupancies, by door. Past residents
              are on each unit&apos;s history.
            </SectionNote>
            {renderResidents()}
          </RosterSection>

          <RosterSection aria-label="Visitors inside">
            <SectionHead>
              <SectionTitle>Visitors inside</SectionTitle>
              {maySeeVisitors && (
                <SectionCount>
                  {term
                    ? `${visitors.length} of ${visitorTotal} shown`
                    : `${visitorTotal} open ${
                        visitorTotal === 1 ? 'arrival' : 'arrivals'
                      }`}
                </SectionCount>
              )}
            </SectionHead>
            <SectionNote>
              People who signed in and have no departure recorded. This is not a
              count of who is in the building — it is a count of arrivals nobody
              has closed.
            </SectionNote>
            {/* THE GHOST WARNING. An arrival still open from a previous local
                day was never observed leaving: the nightly sweep closes an entry
                at local 23:59, so anything older than today either predates the
                sweep or the sweep has not reached it. Either way nobody watched
                that person go and nobody has confirmed they are still here.
                Shown above the list so a reader who never scrolls has still been
                told, and worded as work to do rather than as a fact about who is
                present. */}
            {maySeeVisitors && staleTotal > 0 && (
              <Banner tone="warning">
                {staleTotal === 1
                  ? '1 of these arrived before today and was never signed out. '
                  : `${staleTotal} of these arrived before today and were never signed out. `}
                Nobody recorded them leaving, so treat them as unaccounted for
                rather than as present.
                {arrivedTodayTotal > 0
                  ? ` ${arrivedTodayTotal} ${
                      arrivedTodayTotal === 1 ? 'arrival' : 'arrivals'
                    } on today's date.`
                  : ' None of these arrived today.'}
              </Banner>
            )}
            {renderVisitors()}
          </RosterSection>
        </Staleable>
      )}
      </StateSwitch>
    </Wrap>
  );
};
